import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { Link, Text } from "@backstage/ui";
import {
	AVD_PLUGIN_ID,
	BUNDLE_RECLAMATIONS_PATH,
	type BundleInventoryItem,
	type BundleReclamationRecord,
	type MyBundleReclamationsResponse,
} from "@internal/backstage-plugin-avd-common";
import { ReactNode, useEffect, useState } from "react";

import { absoluteTime } from "./inventoryCells";

export type History = { rows?: BundleReclamationRecord[]; error?: string };

/** The reclamations of one bundle; `refreshKey` re-reads them when the bundle changes underneath. */
export function useReclamationHistory(item: BundleInventoryItem, refreshKey = ""): History {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [history, setHistory] = useState<History>({});
	const { slug, bundleType, bundleId } = item;

	useEffect(() => {
		let active = true;
		// Another bundle's rows or error must not show while this one's request is pending.
		setHistory({});
		const load = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const query = new URLSearchParams({ slug, bundleType, bundleId: bundleId ?? "" });
				const res = await fetchApi.fetch(`${baseUrl}${BUNDLE_RECLAMATIONS_PATH}?${query}`);
				if (!res.ok) throw new Error(`The reclamation log responded ${res.status}.`);
				const body = (await res.json()) as MyBundleReclamationsResponse;
				if (active) setHistory({ rows: body.items ?? [] });
			} catch (e) {
				if (active) {
					setHistory({
						error:
							e instanceof Error ? e.message : "The reclamation log is unreachable.",
					});
				}
			}
		};
		void load();
		return () => {
			active = false;
		};
	}, [discoveryApi, fetchApi, slug, bundleType, bundleId, refreshKey]);

	return history;
}

/** A bundle's reclamation history, newest first. A failed read is said, never shown as "none". */
export function ReclamationHistory(props: Readonly<{ history: History }>): ReactNode {
	const { rows, error } = props.history;
	if (error) {
		return (
			<Text as="p" color="danger" role="alert">
				Couldn't read the reclamation history: {error}
			</Text>
		);
	}
	if (!rows) return <Text as="p">Reading reclamation history…</Text>;
	if (rows.length === 0) {
		return (
			<Text as="p" color="secondary">
				No reclamations recorded for this bundle.
			</Text>
		);
	}
	return (
		<ul>
			{rows.map((row) => (
				<li key={row.id}>
					<Text as="span">
						{absoluteTime(row.recordedAt)} · {row.daysIdle} d idle ·{" "}
						{row.enforced ? "reclaimed" : "dry run"}
						{row.notices.length ? ` · ${row.notices.length} notice(s)` : ""}
						{row.runUrl ? (
							<>
								{" · "}
								<Link href={row.runUrl} target="_blank" rel="noopener noreferrer">
									run
								</Link>
							</>
						) : null}
					</Text>
				</li>
			))}
		</ul>
	);
}
